import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { List } from 'immutable';
import { WindowRefService } from 'i2g-ng-utils';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { IIcon } from '../icon/icon.interface';
import { IconSizesEnum } from '../icon/icons-sizes.enum';

export enum DropdownHorizontalAlignmentsEnum {
  left = 'left',
  right = 'right',
}

export enum DropdownVerticalAlignmentsEnum {
  top = 'top',
  bottom = 'bottom',
}

// TODO: make dropdown accessible (or at least keyboard-friendly)
// TODO: to intelligently calculate vAlignment post v2 (it should be checked onResizeWindow)
@Component({
  selector: 'app-dropdown',
  templateUrl: 'dropdown.component.html',
  styleUrls: ['dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent implements OnInit, OnDestroy {
  @Input() triggerLabel: string;
  @Input() horizontalAlignment: DropdownHorizontalAlignmentsEnum =
    DropdownHorizontalAlignmentsEnum.left;
  @Input() verticalAlignment: DropdownVerticalAlignmentsEnum =
    DropdownVerticalAlignmentsEnum.bottom;
  @Input() dropdownGroupedItems: List<List<any>>;
  @Input() dropdownId: string;
  @Input() disabled: boolean;
  @Input() triggerIcon: IIcon;
  @Input() triggerBadge: string;
  @Input() showChevron: boolean = true;
  @Input() dynamicVAlignment: boolean;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() stopClickPropagation: boolean;
  @Input() trackingAdditionalProperties: any;
  @Input() e2eId: string;

  @ContentChild(TemplateRef, { static: true }) template: TemplateRef<any>;

  private _el: any;
  private _window: any;
  private _initialVAlignment: string;

  isOpen = false;
  closeCallback: Function = this.close.bind(this);
  hAlignment: string;
  vAlignment: string;
  chevronDownIcon: IIcon;
  dropdownItems: List<List<any>>;

  constructor(
    private _elementRef: ElementRef,
    @Inject(DOCUMENT) private _document: Document,
    private _changeDetectorRef: ChangeDetectorRef,
    private windowRefService: WindowRefService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const dropdownGroupedItemsChanges = changes.dropdownGroupedItems;

    if (dropdownGroupedItemsChanges) {
      if (this.verticalAlignment === DropdownVerticalAlignmentsEnum.top) {
        this.dropdownItems = this.dropdownGroupedItems
          .reverse()
          .map((dropdownItems) => dropdownItems.reverse()) as List<List<any>>;
      } else {
        this.dropdownItems = this.dropdownGroupedItems;
      }
    }
  }

  ngOnInit() {
    const vAlignment = this.verticalAlignment;

    this._initialVAlignment = vAlignment;
    this._el = this._elementRef.nativeElement;
    this._window = this.windowRefService.nativeWindow;

    this.hAlignment = this.horizontalAlignment;
    this.vAlignment = vAlignment;
    this.chevronDownIcon = {
      name: 'chevron-down',
      size: IconSizesEnum.small,
    };
  }

  toggleDropdown(evt: Event) {
    if (this.stopClickPropagation) {
      evt.stopPropagation();
    }

    evt.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  close() {
    this._unsubscribeDropdown();

    this.isOpen = false;

    if (this.dynamicVAlignment && this.vAlignment !== this._initialVAlignment) {
      this.vAlignment = this._initialVAlignment;
    }

    this._changeDetectorRef.markForCheck();
  }

  open() {
    if (!this.disabled) {
      // - we're dispatching a CustomEvent('dropdown-opened') here so other opened dropdowns are closed.
      // - CustomEvent is not supported on IE11 or versions below.
      if (typeof this._window.CustomEvent === 'function') {
        this._document.dispatchEvent(new CustomEvent('dropdown-opened'));
      }
      this._unsubscribeDropdown();

      this.isOpen = true;

      if (this.dynamicVAlignment) {
        this._changeDetectorRef.detectChanges();

        this.vAlignment = this.getBestVAlignment();
      }

      setTimeout(() => this._subscribeDropdown());

      this._changeDetectorRef.markForCheck();
    }
  }

  // TODO: to intelligently calculate vAlignment post v2 (check onResizeWindow and compare space up x space down)
  getBestVAlignment(): string {
    const windowHeight = this._window.innerHeight;
    const dropdownBottom = this._el
      .querySelector('.dropdown-contents')
      .getBoundingClientRect().bottom;

    return DropdownVerticalAlignmentsEnum[
      windowHeight > dropdownBottom
        ? DropdownVerticalAlignmentsEnum.bottom
        : DropdownVerticalAlignmentsEnum.top
    ];
  }

  ngOnDestroy() {
    this._unsubscribeDropdown();
  }

  private _subscribeDropdown() {
    this._document.addEventListener('click', this._handleClickOutsideCallback);
    this._document.addEventListener(
      'dropdown-opened',
      this._handleClickOutsideCallback
    );
  }

  private _unsubscribeDropdown() {
    this._document.removeEventListener(
      'click',
      this._handleClickOutsideCallback
    );
    this._document.removeEventListener(
      'dropdown-opened',
      this._handleClickOutsideCallback
    );
  }

  private _handleClickOutside(evt: Event) {
    const evtTarget = evt.target;

    if (!evtTarget) {
      return;
    }

    if (!this._el.contains(evtTarget)) {
      this.close();
    }
  }

  private _handleClickOutsideCallback = this._handleClickOutside.bind(this);
}
